/**
 * AppEnvClient factory for the desktop shell.
 *
 * Wraps the kernel-routed `os.app.config.*` NATS subjects (shipped as
 * `APP_CONFIG_SUBJECTS` from `@khal-os/sdk/app`, see commit 2317fdf) into the
 * `AppEnvClient` interface that `AppSettingsPanel` / `AppEnvPanel` consume.
 *
 * The kernel handler is `repos/core/src/lib/app-config/handlers.ts`; replies
 * follow the `AppEnv*Reply` discriminated unions in
 * `@khal-os/sdk/app/app-config`. We narrow with `isAppEnvError` and translate
 * field names to match the desktop-shell `AppEnvClient` shape.
 */
import type {
	AppEnvClient,
	AppEnvListResult,
	AppEnvSaveEntry,
	AppEnvSaveResult,
} from '@khal-os/desktop-shell';
import {
	APP_CONFIG_SUBJECTS,
	getNatsClient,
	isAppEnvError,
	type AppEnvListReply,
	type AppEnvSetReply,
	type AppEnvRevealReply,
	type AppEnvDeleteReply,
	type AppEnvClaimReply,
	type AppEnvClaimSkipReply,
} from '@khal-os/sdk/app';

const TIMEOUT_MS = 8000;

async function natsRequest<T>(subject: string, payload: unknown): Promise<T> {
	const client = getNatsClient();
	const raw = await client.request(subject, payload, TIMEOUT_MS);
	return (typeof raw === 'string' ? JSON.parse(raw) : raw) as T;
}

function unwrap<T>(reply: T | { error: string }): T {
	if (isAppEnvError(reply as T | { error: string })) {
		throw new Error((reply as { error: string }).error);
	}
	return reply as T;
}

export const appEnvClient: AppEnvClient = {
	async list({ installedAppId }) {
		const reply = await natsRequest<AppEnvListReply>(APP_CONFIG_SUBJECTS.get, { installedAppId });
		const ok = unwrap(reply);
		const result: AppEnvListResult = {
			values: ok.values.map((v) => ({
				key: v.key,
				type: v.type,
				masked_value: v.masked_value,
				last_modified: v.last_modified,
			})),
			claim_pending: ok.claim_pending,
			claim_keys: ok.claim_keys,
		};
		return result;
	},

	async save({ installedAppId, entries, dry_run }) {
		const payload: { installedAppId: string; entries: AppEnvSaveEntry[]; dry_run?: boolean } = {
			installedAppId,
			entries,
		};
		if (dry_run !== undefined) payload.dry_run = dry_run;
		const reply = await natsRequest<AppEnvSetReply>(APP_CONFIG_SUBJECTS.save, payload);
		const ok = unwrap(reply);
		const result: AppEnvSaveResult = { saved: ok.applied };
		return result;
	},

	async reveal({ installedAppId, key }) {
		const reply = await natsRequest<AppEnvRevealReply>(APP_CONFIG_SUBJECTS.reveal, {
			installedAppId,
			key,
		});
		const ok = unwrap(reply);
		return { value: ok.value };
	},

	async delete({ installedAppId, key }) {
		const reply = await natsRequest<AppEnvDeleteReply>(APP_CONFIG_SUBJECTS.delete, {
			installedAppId,
			key,
		});
		const ok = unwrap(reply);
		return { deleted: ok.deleted };
	},

	async claim({ installedAppId, sourceKeys }) {
		const reply = await natsRequest<AppEnvClaimReply>(APP_CONFIG_SUBJECTS.claim, {
			installedAppId,
			sourceKeys,
		});
		const ok = unwrap(reply);
		return { adopted: ok.claimed };
	},

	async claim_skip({ installedAppId }) {
		const reply = await natsRequest<AppEnvClaimSkipReply>(APP_CONFIG_SUBJECTS.claim_skip, {
			installedAppId,
		});
		const ok = unwrap(reply);
		return { skipped: ok.skipped };
	},
};
